import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ListBox, ListBoxItem, type Selection } from "react-aria-components";
import { FaCheck } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { VStack, HStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { apiClient } from "../../../zodios/client";

type Props = {
  adId: number;
  selectedAdGroupIds: string[];
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onSelectionChange: (adGroupIds: string[]) => void;
};

export function AdGroupSelector({ adId, selectedAdGroupIds, isExpanded, onExpandedChange, onSelectionChange }: Props) {
  const { t } = useTranslation(["advertising"]);
  const adGroupsQuery = useQuery({
    queryKey: ["adGroups", adId],
    queryFn: async () => await apiClient.getAdGroups({ queries: { advertisement: adId } }),
    select: (data) => data.map((ag) => ({ id: ag.ad_group_id, name: ag.ad_group_name })),
  });

  const allAdGroupIds = useMemo(() => adGroupsQuery.data?.map((ag) => ag.id) ?? [], [adGroupsQuery.data]);

  const allSelected = selectedAdGroupIds.length === 0 || selectedAdGroupIds.length === allAdGroupIds.length;

  const selectedKeys: Selection = useMemo(
    () => (allSelected ? "all" : new Set(selectedAdGroupIds)),
    [allSelected, selectedAdGroupIds],
  );

  if (!adGroupsQuery.data || adGroupsQuery.data.length === 0) return null;

  return (
    <VStack paddingY="medium" width="100%" gap="small" alignItems="flex-start">
      <Button
        variant="contentOnly"
        onPress={() => {
          const next = !isExpanded;
          onExpandedChange(next);
          if (!next) onSelectionChange([]);
        }}
      >
        <span className={css({ fontSize: "medium", color: "text.label", fontWeight: "bolder" })}>
          {isExpanded ?
            t(($) => $.advertising["- Apply to all ad groups"])
          : t(($) => $.advertising["+ Customize Ad Groups"])}
        </span>
      </Button>

      {isExpanded && (
        <>
          <HStack width="100%" justifyContent="space-between" alignItems="center">
            <span className={css({ fontSize: "small", fontWeight: "bold", color: "text.label" })}>
              {t(($) => $.advertising["Ad Groups"])}
            </span>
            {!allSelected && (
              <Button variant="contentOnly" onPress={() => onSelectionChange(allAdGroupIds)}>
                <span className={css({ fontSize: "xsmall" })}>{t(($) => $.advertising["Select All"])}</span>
              </Button>
            )}
          </HStack>
          <ListBox
            aria-label={t(($) => $.advertising["Ad Groups"])}
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            onSelectionChange={(selection: Selection) => {
              if (selection === "all") {
                onSelectionChange(allAdGroupIds);
              } else {
                onSelectionChange([...selection].map(String));
              }
            }}
            items={adGroupsQuery.data}
            className={css({
              width: "100%",
              border: "1px solid {colors.gray.200}",
              borderRadius: "standard",
              paddingY: "xsmall",
              maxHeight: "150px",
              overflowY: "auto",
            })}
          >
            {(item) => (
              <ListBoxItem
                id={item.id}
                textValue={item.name}
                className={css({
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "small",
                  paddingY: "xsmall",
                  paddingX: "medium",
                  fontSize: "small",
                  color: "darkblue/80",
                  borderRadius: "standard",
                  _focus: { backgroundColor: "black/5" },
                  _hover: { backgroundColor: "black/5" },
                  _selected: {
                    color: "trellisPalette.4",
                    fontWeight: "bold",
                    _hover: { backgroundColor: "trellisPalette.4/15" },
                  },
                })}
              >
                {({ isSelected }) => (
                  <>
                    <span
                      className={css({
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "16px",
                        height: "16px",
                        borderRadius: "4px",
                        border: isSelected ? "none" : "1px solid {colors.gray.400}",
                        backgroundColor: isSelected ? "trellisPalette.4" : "white",
                        color: "white",
                        fontSize: "xsmall",
                        flexShrink: 0,
                      })}
                    >
                      {isSelected && <FaCheck />}
                    </span>
                    {item.name}
                  </>
                )}
              </ListBoxItem>
            )}
          </ListBox>
          <small className={css({ fontSize: "xsmall", color: "gray.500" })}>
            {allSelected ?
              t(($) => $.advertising["Video will be applied to all ad groups"])
            : t(($) => $.advertising["Video will be applied to selected ad groups"], {
                count: selectedAdGroupIds.length,
              })
            }
          </small>
        </>
      )}
    </VStack>
  );
}
